import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Recurring subscription bundle store.
 *
 * Holds tiers the user wants to purchase together as a single subscription.
 * A billing subscription can contain up to 10 tier items total.
 *
 * Bundle item shape:
 *   {
 *     id: 'crime',              // tier slug, used as key
 *     slug: 'crime',
 *     name: 'Kryminał',
 *     priceMonthly: 59,
 *     image: '...',
 *     quantity: 2,
 *     preferences: { genres: ['crime'] } // optional
 *   }
 */

const MAX_BUNDLE_ITEMS = 10

export const useCheckoutBundleStore = create(
  persist(
    (set, get) => ({
      items: [],

      /**
       * Add a tier to the bundle. If the same slug already exists, increment quantity.
       * Returns true if added/updated, false if it would exceed the max item count.
       */
      addTier: (tier, preferences = null) => {
        const items = get().items
        const totalCount = items.reduce((sum, item) => sum + item.quantity, 0)
        if (totalCount >= MAX_BUNDLE_ITEMS) {
          return false
        }

        const existing = items.find((item) => item.slug === tier.id)
        if (existing) {
          set({
            items: items.map((item) =>
              item.slug === tier.id ? { ...item, quantity: item.quantity + 1 } : item
            ),
          })
          return true
        }

        set({
          items: [
            ...items,
            {
              id: tier.id,
              slug: tier.id,
              name: tier.title || tier.name,
              priceMonthly: tier.priceMonthly,
              image: tier.image,
              quantity: 1,
              preferences,
            },
          ],
        })
        return true
      },

      /**
       * Remove a tier entirely from the bundle.
       */
      removeTier: (slug) => {
        set({ items: get().items.filter((item) => item.slug !== slug) })
      },

      /**
       * Change quantity by delta. Removes the item if quantity reaches 0.
       * Respects the max item cap for positive deltas.
       */
      updateQuantity: (slug, delta) => {
        const items = get().items
        const totalCount = items.reduce((sum, item) => sum + item.quantity, 0)
        if (delta > 0 && totalCount >= MAX_BUNDLE_ITEMS) {
          return false
        }

        set({
          items: items
            .map((item) => {
              if (item.slug !== slug) return item
              const nextQuantity = item.quantity + delta
              return nextQuantity > 0 ? { ...item, quantity: nextQuantity } : null
            })
            .filter(Boolean),
        })
        return true
      },

      /**
       * Directly set quantity for a tier. Removes if <= 0.
       */
      setQuantity: (slug, quantity) => {
        const qty = Math.max(0, parseInt(quantity, 10) || 0)
        if (qty === 0) {
          set({ items: get().items.filter((item) => item.slug !== slug) })
          return
        }
        const items = get().items
        const totalWithoutThis = items.reduce(
          (sum, item) => (item.slug === slug ? sum : sum + item.quantity),
          0
        )
        if (totalWithoutThis + qty > MAX_BUNDLE_ITEMS) {
          return false
        }
        set({
          items: items.map((item) =>
            item.slug === slug ? { ...item, quantity: qty } : item
          ),
        })
        return true
      },

      /**
       * Empty the bundle.
       */
      clearBundle: () => set({ items: [] }),

      /**
       * Total monthly price across all items (quantity * price).
       */
      getTotalMonthlyPrice: () => {
        return get().items.reduce((sum, item) => sum + item.priceMonthly * item.quantity, 0)
      },

      /**
       * Number of unique tier slugs in the bundle.
       */
      getUniqueTierCount: () => get().items.length,

      /**
       * Total item count including quantities.
       */
      getTotalItemCount: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0)
      },

      /**
       * True when the bundle is at the 10-item cap.
       */
      isMaxReached: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0) >= MAX_BUNDLE_ITEMS
      },
    }),
    { name: 'swiaty-bundle' }
  )
)
